"use client"

import { countries } from "@/constants/constants"
import { useState, useRef, useEffect } from "react"
import ReactCountryFlag from "react-country-flag"
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { validatePhoneByAreaCode } from "./phone-validation";

interface PhoneInputProps {
  value?: string;
  onChange?: (val: string) => void;
  labelClassName?: string;
  error?: string;
}

export default function PhoneInput({ value, onChange, labelClassName = "", error }: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState(countries[0])
  const [phoneNumber, setPhoneNumber] = useState("")
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const searchInputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Parse value to set country and number on mount and when value changes
  useEffect(() => {
    if (value) {
      // Find the country whose code matches the start of value
      const found = countries.find(c => value.startsWith(c.code + "-") || value.startsWith(c.code + " "));
      if (found) {
        // If the current selected country has the same code, keep it; otherwise use the found one
        if (selectedCountry.code === found.code) {
          // Keep current selection if codes match
        } else {
          setSelectedCountry(found);
        }
        // Remove country code and dash/space
        const num = value.replace(found.code + "-", '').replace(found.code + " ", '');
        setPhoneNumber(num);
      } else {
        setPhoneNumber(value);
      }
    } else {
      setPhoneNumber("");
    }
  }, [value]);

  // Filter countries based on search term
  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.includes(searchTerm) ||
      country.countryCode.toLowerCase().includes(searchTerm.toLowerCase()),
  )

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isDropdownOpen])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
        setSearchTerm("")
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleCountrySelect = (country: (typeof countries)[0]) => {
    setSelectedCountry(country)
    setIsDropdownOpen(false)
    setSearchTerm("")
    // When country changes, update value
    if (onChange) {
      onChange(`${country.code}-${phoneNumber}`)
    }
  }

  /**
   * Validates the current phone number based on the selected country code
   * This is a utility function that can be called externally or used internally
   * @returns ValidationResult with isValid flag and optional error message
   */
  const validateCurrentPhone = () => {
    const fullPhoneNumber = `${selectedCountry.code}-${phoneNumber}`;
    return validatePhoneByAreaCode(fullPhoneNumber, selectedCountry.code);
  }

  return (
    <div className="w-full space-y-2">
      <label htmlFor="phone" className={`text-gray-500  ${labelClassName}`}>
        Phone number *
      </label>
      <div className="flex gap-2">
        {/* Country Code Selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`flex items-center gap-2 px-3 py-2 h-11 bg-white border ${error ? 'border-red-500' : 'border-gray-300'} rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 min-w-[80px]`}
          >
            <ReactCountryFlag
              countryCode={selectedCountry.countryCode}
              svg
              style={{
                width: "20px",
                height: "15px",
              }}
            />
            <span className="text-sm font-medium">{selectedCountry.code}</span>
            <svg
              className={`h-4 w-4 text-gray-500 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-300 rounded-md shadow-lg z-10">
              {/* Search Input */}
              <div className="p-2 border-b border-gray-200">
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search countries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Countries List */}
              <div className="max-h-48 overflow-y-auto">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleCountrySelect(country)}
                      className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50"
                    >
                      <ReactCountryFlag
                        countryCode={country.countryCode}
                        svg
                        style={{
                          width: "20px",
                          height: "15px",
                        }}
                      />
                      <span className="text-sm flex-1">{country.name}</span>
                      <span className="text-sm text-gray-500">{country.code}</span>
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-sm text-gray-500 text-center">No countries found</div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <div className="relative flex-1">
          <input
            id="phone"
            type="tel"
            value={typeof value === 'string' ? value.replace(`${selectedCountry.code}-`, '') : phoneNumber}
            onChange={(e) => {
              // Only allow numeric input
              const numericValue = e.target.value.replace(/\D/g, '');
              setPhoneNumber(numericValue);
              if (onChange) {
                onChange(`${selectedCountry.code}-${numericValue}`);
              }
            }}
            placeholder="Phone number"
            className={`h-11 px-3 py-2 border ${error ? 'border-red-500 pr-10' : 'border-gray-300'} rounded-md w-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500`}
          />
          {error && (
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
            />
          )}
        </div>
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  )
}

// Export the validation function to make it available for external use
export { validatePhoneByAreaCode };